import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import useRedirectLoggedOutUser from "../customHook/useRedirectLoggedOutUser";
import axios from 'axios';
import { toast } from 'react-toastify';
import './UserLocationManagement.css';
import { useNavigate } from 'react-router-dom';

const UserLocationManagement = () => {
    useRedirectLoggedOutUser('/login');
    const navigate = useNavigate();

    const { user } = useSelector((state) => state.auth);
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Search and Filter state
    const [searchTerm, setSearchTerm] = useState('');
    const [showVisibleOnly, setShowVisibleOnly] = useState(false);

    // Form fields
    const [shiftBookingLocation, setShiftBookingLocation] = useState('');
    const [conversationType, setConversationType] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [byDefault, setByDefault] = useState('hide');
    const [availableAllTime, setAvailableAllTime] = useState(false);

    // Fetch all users on mount
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                { withCredentials: true }
            );
            // Filter to show only staff users (non-students)
            const staffUsers = data.filter(u =>
                u.type === 'staff' && u.status === 'Active'
            );
            setUsers(staffUsers);
        } catch (error) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    // Filter users based on search term and visibility toggle
    const filteredUsers = users.filter(user => {
        const matchesSearch = (
            user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user.email?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        const matchesVisibility = showVisibleOnly ? user.byDefault === 'show' : true;

        return matchesSearch && matchesVisibility;
    });

    const handleUserSelect = (user) => {
        setSelectedUser(user);
        // Populate form with user's current values
        setShiftBookingLocation(user.shiftBookingLocation || '');
        setConversationType(user.conversationType || '');
        setDisplayName(user.displayName || '');
        setByDefault(user.byDefault || 'hide');
        setAvailableAllTime(user.availableAllTime || false);
    };

    const handleSave = async () => {
        if (!selectedUser) return;

        setSaving(true);
        try {
            await axios.patch(
                `${process.env.REACT_APP_BACKEND_URL}/api/users/${selectedUser._id}`,
                {
                    shiftBookingLocation,
                    conversationType,
                    displayName,
                    byDefault,
                    availableAllTime
                },
                { withCredentials: true }
            );

            toast.success('User updated successfully!');

            // Refresh users list
            await fetchUsers();

            // Update selected user
            const updatedUser = users.find(u => u._id === selectedUser._id);
            if (updatedUser) {
                setSelectedUser({
                    ...updatedUser,
                    shiftBookingLocation,
                    conversationType,
                    displayName,
                    byDefault,
                    availableAllTime
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update user');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="location-management-page">
            <div className="page-header">
                <button className="back-btn" onClick={() => navigate('/')}>
                    ← Back to Chat
                </button>
                <h2>User Location Management</h2>
            </div>

            <div className="user-management-container">
                <div className="user-management-content">
                    {/* User List */}
                    <div className="user-list-panel">
                        <div className="panel-header">
                            <h4>Staff Users ({filteredUsers.length})</h4>
                            <div className="search-filter-controls">
                                <input
                                    type="text"
                                    placeholder="Search by name or email..."
                                    className="form-control search-input"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <div className="filter-checkbox">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={showVisibleOnly}
                                            onChange={(e) => setShowVisibleOnly(e.target.checked)}
                                        />
                                        Show Visible Only
                                    </label>
                                </div>
                            </div>
                        </div>
                        {loading ? (
                            <p>Loading users...</p>
                        ) : (
                            <div className="user-list">
                                {filteredUsers.map((u) => (
                                    <div
                                        key={u._id}
                                        className={`user-item ${selectedUser?._id === u._id ? 'active' : ''}`}
                                        onClick={() => handleUserSelect(u)}
                                    >
                                        <img src={u.picture} alt={u.name} />
                                        <div className="user-info">
                                            <strong>{u.name}</strong>
                                            <small>{u.email}</small>
                                            {u.byDefault === 'show' && (
                                                <span className="badge badge-success">Visible</span>
                                            )}
                                            {u.availableAllTime && (
                                                <span className="badge badge-primary">Always Available</span>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Edit Form */}
                    <div className="user-edit-panel">
                        {selectedUser ? (
                            <>
                                <h4>Edit Location Fields - {selectedUser.name}</h4>

                                <div className="form-group">
                                    <label>Shift Booking Location</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={shiftBookingLocation}
                                        onChange={(e) => setShiftBookingLocation(e.target.value)}
                                        placeholder="e.g., Park Royal, Online Group 1 - KBM_training1"
                                    />
                                    <small className="form-text">
                                        Must match location name from shift booking system
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Conversation Type</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={conversationType}
                                        onChange={(e) => setConversationType(e.target.value)}
                                        placeholder="e.g., Park Royal, Online Training 1"
                                    />
                                    <small className="form-text">
                                        Used when creating conversations
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Display Name (Optional)</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={displayName}
                                        onChange={(e) => setDisplayName(e.target.value)}
                                        placeholder="e.g., Park Royal, Online Group 1"
                                    />
                                    <small className="form-text">
                                        Name shown in contact list (defaults to user name if empty)
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Show by Default</label>
                                    <div className="toggle-buttons">
                                        <button
                                            className={`toggle-btn ${byDefault === 'show' ? 'active' : ''}`}
                                            onClick={() => setByDefault('show')}
                                        >
                                            Show
                                        </button>
                                        <button
                                            className={`toggle-btn ${byDefault === 'hide' ? 'active' : ''}`}
                                            onClick={() => setByDefault('hide')}
                                        >
                                            Hide
                                        </button>
                                    </div>
                                    <small className="form-text">
                                        If "Show", user appears in default contact list
                                    </small>
                                </div>

                                <div className="form-group">
                                    <label>Available All Time</label>
                                    <div className="toggle-buttons">
                                        <button
                                            className={`toggle-btn ${availableAllTime ? 'active' : ''}`}
                                            onClick={() => setAvailableAllTime(true)}
                                        >
                                            Yes
                                        </button>
                                        <button
                                            className={`toggle-btn ${!availableAllTime ? 'active' : ''}`}
                                            onClick={() => setAvailableAllTime(false)}
                                        >
                                            No
                                        </button>
                                    </div>
                                    <small className="form-text">
                                        If "Yes", user is always available even without shift booking match (fallback user)
                                    </small>
                                </div>

                                <div className="form-actions">
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={saving}
                                    >
                                        {saving ? 'Saving...' : 'Save Changes'}
                                    </button>
                                </div>

                                {/* Current Values Display */}
                                <div className="current-values">
                                    <h5>Current Configuration:</h5>
                                    <table className="table table-sm">
                                        <tbody>
                                            <tr>
                                                <td><strong>Shift Booking Location:</strong></td>
                                                <td>{selectedUser.shiftBookingLocation || <em>Not set</em>}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Conversation Type:</strong></td>
                                                <td>{selectedUser.conversationType || <em>Not set</em>}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Display Name:</strong></td>
                                                <td>{selectedUser.displayName || <em>Not set</em>}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>By Default:</strong></td>
                                                <td>
                                                    <span className={`badge ${selectedUser.byDefault === 'show' ? 'badge-success' : 'badge-secondary'}`}>
                                                        {selectedUser.byDefault || 'hide'}
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td><strong>Available All Time:</strong></td>
                                                <td>
                                                    <span className={`badge ${selectedUser.availableAllTime ? 'badge-primary' : 'badge-secondary'}`}>
                                                        {selectedUser.availableAllTime ? 'Yes' : 'No'}
                                                    </span>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        ) : (
                            <div className="no-selection">
                                <p>Select a staff user from the list to edit their location configuration</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserLocationManagement;
